import { formatMonthlyPrice } from '../utils/pricing';

const PUBLISHED_PLANS_KEY = 'asi:published-plans';
const PRODUCT_FEATURES_KEY = 'asi:product-features';
const PLANS_EVENT = 'asi:published-plans-changed';

function isBrowser() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function safeParse(raw, fallback) {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch (error) {
    return fallback;
  }
}

function readPlans() {
  if (!isBrowser()) {
    return [];
  }
  const raw = window.localStorage.getItem(PUBLISHED_PLANS_KEY);
  const parsed = safeParse(raw, []);
  if (!Array.isArray(parsed)) {
    return [];
  }
  return parsed.filter((plan) => plan && typeof plan === 'object');
}

function readFeatureMap() {
  if (!isBrowser()) {
    return {};
  }
  const raw = window.localStorage.getItem(PRODUCT_FEATURES_KEY);
  const parsed = safeParse(raw, {});
  if (!parsed || typeof parsed !== 'object') {
    return {};
  }
  return parsed;
}

function dispatchPlans(plans) {
  if (!isBrowser() || typeof window.dispatchEvent !== 'function') {
    return;
  }
  const detail = { plans };
  if (typeof window.CustomEvent === 'function') {
    window.dispatchEvent(new CustomEvent(PLANS_EVENT, { detail }));
    return;
  }
  const event = document.createEvent('Event');
  event.initEvent(PLANS_EVENT, false, false);
  event.detail = detail;
  window.dispatchEvent(event);
}

function writePlans(plans) {
  if (!isBrowser()) {
    return;
  }
  window.localStorage.setItem(PUBLISHED_PLANS_KEY, JSON.stringify(plans));
  dispatchPlans(plans);
}

function writeFeatureMap(map) {
  if (!isBrowser()) {
    return;
  }
  window.localStorage.setItem(PRODUCT_FEATURES_KEY, JSON.stringify(map));
}

function normalizePlan(payload) {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const productId = payload.productId || payload.id || null;
  if (!productId) {
    return null;
  }

  const billingPeriod = Number(payload.billingPeriod || payload.period || 1) || 1;
  const billingUnit = payload.billingUnit || payload.periodUnit || 'month';
  const priceAmount = Number(
    payload.priceAmount !== undefined ? payload.priceAmount : payload.price || 0,
  );
  const features = Array.isArray(payload.features)
    ? payload.features.filter((feature) => typeof feature === 'string' && feature.trim())
    : [];

  const planId = payload.planId || productId;
  const name = payload.name || payload.description || 'Plan';
  const description = payload.description || name;
  const priceDisplay = formatMonthlyPrice(priceAmount, billingPeriod, billingUnit);

  return {
    planId,
    productId,
    name,
    description,
    priceAmount,
    priceDisplay,
    billingPeriod,
    billingUnit,
    generationLimit: Number(payload.generationLimit) || 0,
    creditsPerYear: Number(payload.creditsPerYear) || 0,
    bonusCredits: Number(payload.bonusCredits) || 0,
    totalCredits:
      Number(payload.totalCredits) || Number(payload.generationLimit) || 0,
    features,
    updatedAt: new Date().toISOString(),
  };
}

export function getPublishedPlans() {
  return readPlans();
}

export function findPublishedPlan(identifier) {
  if (!identifier) {
    return null;
  }
  const plans = readPlans();
  return (
    plans.find((plan) => plan.planId === identifier || plan.productId === identifier) || null
  );
}

export function setPlanPublished(planPayload, isPublic) {
  const plans = readPlans();
  const normalized = normalizePlan(planPayload);
  const targetId = normalized ? normalized.productId : planPayload && planPayload.productId;

  const index = plans.findIndex((plan) => plan.productId === targetId);

  if (isPublic && normalized) {
    if (index >= 0) {
      plans[index] = {
        ...plans[index],
        ...normalized,
      };
    } else {
      plans.push(normalized);
    }
    writePlans(plans);
    return normalized;
  }

  if (!isPublic && index >= 0) {
    plans.splice(index, 1);
    writePlans(plans);
  }
  return null;
}

export function removePlanByProductId(productId) {
  if (!productId) {
    return false;
  }
  const plans = readPlans();
  const index = plans.findIndex((plan) => plan.productId === productId);
  if (index === -1) {
    return false;
  }
  plans.splice(index, 1);
  writePlans(plans);
  return true;
}

export function subscribeToPublishedPlans(callback) {
  if (!isBrowser() || typeof callback !== 'function') {
    return () => {};
  }

  const handler = (event) => {
    const plans = event && event.detail && Array.isArray(event.detail.plans)
      ? event.detail.plans
      : readPlans();
    callback(plans);
  };

  const storageHandler = (storageEvent) => {
    if (storageEvent && storageEvent.key === PUBLISHED_PLANS_KEY) {
      handler(storageEvent);
    }
  };

  window.addEventListener(PLANS_EVENT, handler);
  window.addEventListener('storage', storageHandler);

  return () => {
    window.removeEventListener(PLANS_EVENT, handler);
    window.removeEventListener('storage', storageHandler);
  };
}

export function getProductFeaturesMap() {
  return readFeatureMap();
}

export function getProductFeatures(productId) {
  if (!productId) {
    return [];
  }
  const map = readFeatureMap();
  const entry = map[productId];
  if (!Array.isArray(entry)) {
    return [];
  }
  return entry;
}

export function setProductFeatures(productId, features) {
  if (!productId) {
    return;
  }
  const map = readFeatureMap();
  const cleaned = Array.isArray(features)
    ? features.filter((feature) => typeof feature === 'string' && feature.trim())
    : [];
  if (cleaned.length) {
    map[productId] = cleaned;
  } else {
    delete map[productId];
  }
  writeFeatureMap(map);

  const plans = readPlans();
  const index = plans.findIndex((plan) => plan.productId === productId);
  if (index >= 0) {
    plans[index] = {
      ...plans[index],
      features: cleaned,
      updatedAt: new Date().toISOString(),
    };
    writePlans(plans);
  }
}
